import { useContext, useRef, useState } from 'react';

import { BottomSheet, Card, Drop, Loader, Tag, Text } from '@hh.ru/magritte-ui';
import { BoltOutlinedSize16, ChevronRightOutlinedSize16 } from '@hh.ru/magritte-ui/icon';
import { usePush } from '@hh.ru/redux-spa-middleware';
import { TranslatedComponent } from 'bloko/common/hooks/useTranslations';

import { ResumeCardActionsContext } from 'src/components/ResumeCard/Actions/Context';
import useOneClickChangeTopicStateHandler from 'src/components/VacancyResponses/api/useOneClickChangeTopicStateHandler';
import translation from 'src/components/translation';
import { useSelector } from 'src/hooks/useSelector';
import { StateId } from 'src/models/employerNegotiations/stateId.types';
import { NegotiationTopicFunnelStage } from 'src/models/negotiationTopic.types';
import { NON_BREAKING_SPACE } from 'src/utils/constants/symbols';

import SubstateItem from 'src/components/ChangeTopicDropStageItem/SubstateItem';

import styles from './index.less';

const TrlKeys: Record<StateId | 'oneClickTag', string> = {
    [StateId.DiscardByEmployer]: 'negotiations.states.discardByEmployer.softForm',
    [StateId.DiscardByApplicant]: 'negotiations.states.discardByApplicant',
    [StateId.DiscardNoInteraction]: 'negotiations.states.discardNoInteraction',
    [StateId.DiscardToOtherVacancy]: 'negotiations.states.discardToOtherVacancy',
    [StateId.DiscardVacancyClosed]: 'negotiations.states.discardVacancyClosed',
    [StateId.PhoneInterview]: 'negotiations.states.phone_interview',
    [StateId.Assessment]: 'negotiations.states.assessment',
    [StateId.Interview]: 'negotiations.states.interview',
    [StateId.Offer]: 'negotiations.states.offer',
    [StateId.Hired]: 'negotiations.states.hired',
    [StateId.Consider]: 'negotiations.states.consider',
    oneClickTag: 'employer.vacancyResponses.changeTopic.button.oneClick.tag',
};

const HOVER_CLOSE_TIMEOUT = 250;

interface ChangeTopicDropStageItemProps {
    stateName: StateId;
    topicId?: string;
    lastFunnelStage?: NegotiationTopicFunnelStage;
    sendAnalytics?: (name: StateId) => void;
}

const CHANGE_TOPIC_BASE_URL = '/employer/negotiations/change_topic';

const ChangeTopicDropStageItem: TranslatedComponent<ChangeTopicDropStageItemProps> = ({
    trls,
    stateName,
    topicId,
    lastFunnelStage,
    sendAnalytics,
}) => {
    const [isSubstatesMenuVisible, setMenuVisible] = useState(false);

    const { resume, vacancyId } = useContext(ResumeCardActionsContext);

    const activatorRef = useRef(null);
    const abortController = useRef<AbortController | null>(null);
    const timeout = useRef<ReturnType<typeof setTimeout>>();

    const { handleSubmit } = useOneClickChangeTopicStateHandler(abortController);
    const push = usePush();

    const { isLoading, funnelStageId: id } =
        useSelector((state) => state.candidatesList?.loadingChangeTopicState) || {};
    const funnel = useSelector((state) => state.vacancyFunnel);
    const funnelIdFromQuery = useSelector(({ router }) => router.location.query.funnelId);

    const currentStage = funnel?.stages?.find((stage) => stage.state.toLowerCase() === stateName);
    const isOneClick = currentStage?.quickTransferToStageEnabled;
    const substates = currentStage?.substates;

    if (
        lastFunnelStage &&
        currentStage &&
        ((!substates && lastFunnelStage?.funnelStageId === currentStage.funnelStageId) ||
            (substates?.length === 1 && lastFunnelStage?.funnelStageId === substates[0].funnelStageId))
    ) {
        return null;
    }

    const trlKey = TrlKeys[stateName];

    const handleOpenMenu = () => {
        if (timeout.current) {
            clearTimeout(timeout.current);
        }
        setMenuVisible(true);
    };

    const handleCloseMenu = () => {
        timeout.current = setTimeout(() => setMenuVisible(false), HOVER_CLOSE_TIMEOUT);
    };

    const handleClickChangeTopicButton = (quickTransferToStageEnabled?: boolean, funnelStageId?: number) => {
        sendAnalytics?.(stateName);

        if (quickTransferToStageEnabled) {
            const values = {
                resumeHash: resume._attributes.hash,
                state: stateName,
                t: topicId || '',
                vacancyId: Number(vacancyId),
                funnelStageId,
                trls,
                ...(funnelIdFromQuery && {
                    funnelId: funnelIdFromQuery,
                }),
            };
            void handleSubmit(values);
        } else {
            let url = `${CHANGE_TOPIC_BASE_URL}?t=${topicId}&state=${stateName}`;
            if (funnelIdFromQuery) {
                url += `&funnelId=${funnelIdFromQuery}`;
            } else {
                url += `&vacancyId=${vacancyId}`;
            }
            if (funnelStageId) {
                url += `&collectionId=${stateName}_${funnelStageId}`;
            }
            push(url);
        }
    };

    const dropContent = substates?.map(({ substateName, funnelStageId, quickTransferToStageEnabled }) => {
        if (lastFunnelStage?.funnelStageId === funnelStageId) {
            return null;
        }

        return (
            <SubstateItem
                key={substateName}
                name={substateName}
                funnelStageId={funnelStageId}
                quickTransferToStageEnabled={quickTransferToStageEnabled}
                onClick={handleClickChangeTopicButton}
            />
        );
    });

    return (
        <>
            <Card
                {...(substates && { ref: activatorRef })}
                onClick={
                    substates
                        ? handleOpenMenu
                        : () => handleClickChangeTopicButton(isOneClick, currentStage?.funnelStageId)
                }
                borderRadius={16}
                padding={12}
                paddingLeft={16}
                hoverStyle="secondary"
                stretched
            >
                <div className={styles.buttonWrapper}>
                    {isLoading && id === currentStage?.funnelStageId && (
                        <>
                            <Loader initial="primary" size={16} />
                            {NON_BREAKING_SPACE}
                        </>
                    )}
                    <Text style="primary" typography="label-2-regular">
                        {trls[trlKey]}
                    </Text>
                    {!substates && isOneClick && (
                        <>
                            {NON_BREAKING_SPACE}
                            <Tag icon={BoltOutlinedSize16} style="special">
                                {trls[TrlKeys.oneClickTag]}
                            </Tag>
                        </>
                    )}
                    {substates && <ChevronRightOutlinedSize16 />}
                </div>
            </Card>
            {substates?.length && (
                <>
                    <Drop
                        placement="right-top"
                        visible={isSubstatesMenuVisible}
                        onClose={() => setMenuVisible(false)}
                        onMouseEnter={handleOpenMenu}
                        onMouseLeave={handleCloseMenu}
                        activatorRef={activatorRef}
                        space={0}
                    >
                        <div className={styles.drop}>{dropContent}</div>
                    </Drop>
                    <BottomSheet visible={isSubstatesMenuVisible} onClose={() => setMenuVisible(false)}>
                        {dropContent}
                    </BottomSheet>
                </>
            )}
        </>
    );
};

export default translation(ChangeTopicDropStageItem);
